import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col, Alert } from "react-bootstrap";
import { FaTrashAlt } from "react-icons/fa";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateClient, getContactByClientId, createContact, updateClientContact, deleteClientContact } from "../../../api/CLIENTS/clients";
import { toast } from "react-toastify";
import MarkdownEditor from "../../MarkdownEditor/MarkdownEditor";

const normalizePhone = (phone) => (phone ?? "").replace(/\s+/g, "");

const EditClient = ({ show, onClose, client }) => {
    const queryClient = useQueryClient();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState("");
    const [deletedContactIds, setDeletedContactIds] = useState([]);
    const [formData, setFormData] = useState({
        nom: "",
        adresse: "",
        email: "",
        originalEmail: "",
        telephone: "",
        originalTelephone: "",
        informations: "",
        alertes: "",
        parent: null,
    });

    const [contacts, setContacts] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(false);
    const CONTACT_TYPE_OPTIONS = [
        { value: "contact", label: "Contact" },
        { value: "billing", label: "Facturation" },
    ];

    useEffect(() => {
        if (!client) return;
        const normalizedClient = client.data ? client.data : client;

        setFormData({
            nom: normalizedClient.name ?? normalizedClient.nom ?? "",
            adresse: normalizedClient.address ?? normalizedClient.adresse ?? "",
            email: normalizedClient.email ?? "",
            originalEmail: normalizedClient.email ?? "",
            telephone: normalizePhone(normalizedClient.phone_number ?? normalizedClient.telephone ?? ""),
            originalTelephone: normalizePhone(normalizedClient.phone_number ?? normalizedClient.telephone ?? ""),
            informations: normalizedClient.information ?? normalizedClient.informations ?? "",
            alertes: normalizedClient.alert ?? normalizedClient.alertes ?? "",
            parent: normalizedClient.parent ?? null,
        });
    }, [client]);

    useEffect(() => {
        const fetchContacts = async () => {
            if (!client?.id || !show) return;

            setLoadingContacts(true);
            setContacts([]);
            try {
                const contactData = await getContactByClientId(client.id);
                const payload = contactData?.data ?? contactData;
                const normalized = Array.isArray(payload?.contacts)
                    ? payload.contacts
                    : Array.isArray(payload)
                    ? payload
                    : [];

                setContacts(
                    normalized.map((c) => ({
                        id: c.id ?? null,
                        nom: c.name ?? c.nom ?? "",
                        email: c.email ?? "",
                        originalEmail: c.email ?? "",
                        telephone: normalizePhone(c.phone_number ?? c.telephone ?? ""),
                        originalTelephone: normalizePhone(c.phone_number ?? c.telephone ?? ""),
                        type: c.type ?? "contact",
                    }))
                );
            } catch (error) {
                console.error("Erreur lors de la récupération des contacts :", error);
                setContacts([]);
            } finally {
                setLoadingContacts(false);
            }
        };

        fetchContacts();
    }, [client?.id, show]);

    const mutation = useMutation({
        mutationFn: (data) => updateClient(client.id, data),
        onError: (error) => {
            setFormError(error?.message || "Erreur lors de la mise à jour du client.");
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleContactChange = (index, field, value) => {
        setContacts((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const addContact = () => {
        setContacts((prev) => [...prev, { id: null, nom: "", email: "", telephone: "", originalTelephone: "", type: "contact" }]);
    };

    const removeContact = (index) => {
        const contact = contacts[index];
        if (contact.id) {
            setDeletedContactIds((prev) => [...prev, contact.id]);
        }
        setContacts((prev) => prev.filter((_, i) => i !== index));
    };

    const validateEmail = (email) => {
        if (!email) return true;
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError("");

        if (formData.email && !validateEmail(formData.email)) {
            setFormError("Adresse e-mail client invalide.");
            return;
        }

        for (let i = 0; i < contacts.length; i++) {
            const c = contacts[i];
            if (c.email && !validateEmail(c.email)) {
                setFormError(`Adresse e-mail invalide pour le contact ${i + 1}.`);
                return;
            }
        }

        const clientPayload = {
            client: {
                nom: formData.nom,
                adresse: formData.adresse,
                ...(formData.email !== formData.originalEmail && { email: formData.email }),
                ...(formData.telephone !== formData.originalTelephone && { telephone: formData.telephone || null }),
                informations: formData.informations,
                alertes: formData.alertes,
                parent: formData.parent ?? null,
            },
        };

        setIsSubmitting(true);
        try {
            const updatedClientResponse = await mutation.mutateAsync(clientPayload);
            const updatedClientId = updatedClientResponse?.data?.id ?? updatedClientResponse?.id ?? client.id;

            await Promise.all(
                deletedContactIds.map((contactId) =>
                    deleteClientContact(updatedClientId, contactId)
                )
            );

            const contactPromises = contacts.map(async (c) => {
                if (!c.nom && !c.email && !c.telephone) return null;

                try {
                    if (c.id) {
                        const updatePayload = {
                            contact: {
                                nom: c.nom,
                                ...(c.email !== c.originalEmail && { email: c.email }),
                                ...(c.telephone !== c.originalTelephone && { telephone: c.telephone || null }),
                                type: c.type,
                            }
                        };
                        return await updateClientContact(updatedClientId, c.id, updatePayload);
                    } else {
                        const createPayload = {
                            contact: {
                                nom: c.nom,
                                email: c.email,
                                telephone: c.telephone || null,
                                type: c.type,
                            },
                        };
                        return await createContact(updatedClientId, createPayload);
                    }
                } catch (err) {
                    console.error(`Erreur lors de la sauvegarde du contact ${c.nom}:`, err);
                    throw new Error(`Erreur contact "${c.nom}": ${err.message}`);
                }
            });

            await Promise.all(contactPromises.filter(p => p !== null));

            queryClient.invalidateQueries({ queryKey: ["client", String(updatedClientId)] });
            queryClient.invalidateQueries({ queryKey: ["clients"] });
            onClose();
            toast.success("Client mis à jour avec succès !");
        } catch (err) {
            console.error("Erreur lors de la sauvegarde :", err);
            setFormError(err?.message || "Erreur lors de la sauvegarde.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setFormError("");
        setDeletedContactIds([]);
        onClose();
        mutation.reset();
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>Modification du client</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {formError && <Alert variant="danger">{formError}</Alert>}

                    <Form.Label className="fw-bold">Informations client</Form.Label>

                    <Form.Control
                        className="mb-3"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        placeholder="Nom"
                        required
                    />

                    <Form.Control
                        className="mb-3"
                        name="adresse"
                        value={formData.adresse}
                        onChange={handleChange}
                        placeholder="Adresse"
                    />

                    <Row className="mb-3">
                        <Col>
                            <Form.Control
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Courriel"
                            />
                        </Col>
                        <Col>
                            <Form.Control
                                name="telephone"
                                value={formData.telephone}
                                onChange={handleChange}
                                placeholder="Téléphone"
                            />
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col>
                            <Form.Label className="text-muted">Informations complémentaires</Form.Label>
                            <MarkdownEditor
                                value={formData.informations}
                                onChange={(val) => setFormData((prev) => ({ ...prev, informations: val }))}
                                rows={3}
                                placeholder="Contexte, préférences, informations utiles…"
                            />
                        </Col>
                        <Col>
                            <Form.Label className="text-muted">Alerte</Form.Label>
                            <MarkdownEditor
                                value={formData.alertes}
                                onChange={(val) => setFormData((prev) => ({ ...prev, alertes: val }))}
                                rows={3}
                                placeholder="Points d'attention, contraintes importantes…"
                            />
                        </Col>
                    </Row>

                    <hr />

                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <Form.Label className="fw-bold mb-0">Contacts</Form.Label>
                        <Button variant="primary" size="sm" onClick={addContact}>
                            Ajouter un contact
                        </Button>
                    </div>

                    {loadingContacts && <div className="text-muted mb-3">Chargement des contacts...</div>}
                    {!loadingContacts && contacts.length === 0 && <div className="text-muted mb-3">Aucun contact</div>}

                    {!loadingContacts && contacts.map((c, idx) => (
                        <div key={`${c.id ?? 'new'}-${idx}`} className="mb-3 mt-3">
                            <Row className="align-items-center">
                                <Col md={2}>
                                    <Form.Select
                                        value={c.type}
                                        onChange={(e) => handleContactChange(idx, "type", e.target.value)}
                                    >
                                        {CONTACT_TYPE_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Control
                                        value={c.nom}
                                        onChange={(e) => handleContactChange(idx, "nom", e.target.value)}
                                        placeholder={`Nom du contact ${idx + 1}`}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Control
                                        value={c.email}
                                        onChange={(e) => handleContactChange(idx, "email", e.target.value)}
                                        placeholder="Courriel"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Control
                                        value={c.telephone}
                                        onChange={(e) => handleContactChange(idx, "telephone", e.target.value)}
                                        placeholder="Téléphone"
                                    />
                                </Col>
                                <Col md={2} className="text-end">
                                    <Button variant="danger" size="sm" onClick={() => removeContact(idx)}>
                                        <FaTrashAlt style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                        Supprimer
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    ))}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" disabled={isSubmitting} onClick={handleClose}>
                        Annuler
                    </Button>
                    <Button variant="primary" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? "Chargement..." : "Enregistrer"}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default EditClient;